/**
 * File collection from drops and pickers.
 *
 * The common case is a user dragging in the whole Arduino `build/` folder, so
 * directory entries are walked recursively. Folder drops are filtered to .bin
 * (the .elf/.map/.json siblings are never flashable), while explicitly picked
 * files are always kept so the analyzer can explain why a wrong pick is wrong
 * instead of silently dropping it.
 */

const FLASHABLE_EXTENSION = /\.bin$/i;
const MAX_DIRECTORY_ENTRIES = 200;

function readEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function walkEntry(entry: FileSystemEntry, collected: File[]): Promise<void> {
  if (collected.length >= MAX_DIRECTORY_ENTRIES) return;

  if (entry.isFile) {
    const file = await fileFromEntry(entry as FileSystemFileEntry);
    if (FLASHABLE_EXTENSION.test(file.name)) collected.push(file);
    return;
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries returns at most 100 items per call and signals completion
    // with an empty batch, so it has to be drained in a loop.
    let batch = await readEntries(reader);
    while (batch.length > 0) {
      for (const child of batch) {
        await walkEntry(child, collected);
      }
      batch = await readEntries(reader);
    }
  }
}

export async function filesFromDataTransfer(transfer: DataTransfer): Promise<File[]> {
  const items = Array.from(transfer.items).filter((item) => item.kind === "file");

  const entries = items
    .map((item) => (typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null);

  // Browsers without the entries API still give us a flat file list.
  if (entries.length === 0) {
    return Array.from(transfer.files);
  }

  const collected: File[] = [];
  for (const entry of entries) {
    if (entry.isFile) {
      // A directly dropped file is an explicit choice: keep it whatever it is.
      collected.push(await fileFromEntry(entry as FileSystemFileEntry));
    } else {
      await walkEntry(entry, collected);
    }
  }
  return collected;
}

export async function readFileBytes(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}
